function snKey(value) {
    return String(value || '')
        .trim()
        .toLowerCase();
}

function extrasSn(robot) {
    if (!robot || typeof robot !== 'object') return '';
    const { settings } = robot;
    if (!settings || typeof settings !== 'object') return '';
    const extras =
        settings.extras ||
        (settings.settings && settings.settings.extras) ||
        null;
    return extras && extras.sn ? String(extras.sn).trim() : '';
}

function puduSnFromRobot(robot) {
    return (
        extrasSn(robot) ||
        (robot && robot.sn ? String(robot.sn).trim() : '') ||
        (robot && robot.serial ? String(robot.serial).trim() : '')
    );
}

function taskDeviceSn(task) {
    if (!task || typeof task !== 'object') return '';
    return String(
        task.sn || task.device_sn || task.robot_sn || task.SN || ''
    ).trim();
}

/**
 * Converte a lista de robôs em dados compatíveis com o gráfico de barras de comparação.
 * Itera sobre a lista de relatórios de limpeza e mapeia as métricas por robô para exibição.
 *
 * @param {Array} list Lista de tarefas.
 * @param {Array} cc1Robots Lista de robôs (usados como base do eixo X).
 * @param {Object} intl Instância do useIntl() para formatação de strings internacionalizadas.
 * @returns {Array<Object>} Array de objetos de métricas comparativas por robô.
 */
export function buildRobotComparisonDataFromList(list, cc1Robots, intl) {
    if (
        !list ||
        !Array.isArray(list) ||
        !cc1Robots ||
        !Array.isArray(cc1Robots)
    ) {
        return [];
    }

    const robotStats = {};

    cc1Robots.forEach(r => {
        if (r && r.id) {
            let fallbackName = `Robot ${r.id}`;
            if (intl) {
                fallbackName = intl.formatMessage(
                    {
                        id:
                            'page.robots.cleaning_report.robot_comparison.robot_name_fallback',
                    },
                    { id: r.id }
                );
            }

            robotStats[r.id] = {
                robot_id: r.id,
                sn: puduSnFromRobot(r),
                code: String(r.code || ''),
                name: r.name || r.code || fallbackName,
                tasks: 0,
                time_seconds: 0,
                area: 0,
                daysActive: new Set(),
            };
        }
    });

    list.forEach(task => {
        const robotId = task._robot && task._robot.id;

        if (robotId && robotStats[robotId]) {
            robotStats[robotId].tasks += 1;
            robotStats[robotId].time_seconds += Number(task.clean_time) || 0;
            robotStats[robotId].area += Number(task.clean_area) || 0;
            const fromTask = taskDeviceSn(task);
            if (fromTask) {
                robotStats[robotId].sn = fromTask;
            }

            let dayStr = '';
            if (task.create_time) {
                const [datePart] = String(task.create_time).split('T');
                [dayStr] = datePart.split(' ');
            }
            if (dayStr) {
                robotStats[robotId].daysActive.add(dayStr);
            }
        }
    });

    return Object.values(robotStats).map(stat => {
        const freq = stat.daysActive.size;
        return {
            robot_id: stat.robot_id,
            sn: stat.sn,
            code: stat.code,
            name: stat.name,
            tasks: stat.tasks,
            time: stat.time_seconds
                ? Number((stat.time_seconds / 3600).toFixed(2))
                : 0,
            area: stat.area,
            frequency: freq,
            water: 0,
            energy: 0,
        };
    });
}

export function mergeConsumptionIntoRobotComparison(data, consumption) {
    if (!Array.isArray(data) || !data.length) return data || [];
    const bySn = {};
    const byName = {};
    ((consumption && consumption.by_robot) || []).forEach(row => {
        if (!row) return;
        if (row.sn) bySn[snKey(row.sn)] = row;
        if (row.name) byName[snKey(row.name)] = row;
    });
    return data.map(item => {
        const keys = [item.sn, item.code, item.name].map(snKey).filter(Boolean);
        let row = null;
        keys.forEach(key => {
            if (row) return;
            row = bySn[key] || byName[key] || null;
        });
        return {
            ...item,
            water: row
                ? Number(((Number(row.water_ml) || 0) / 1000).toFixed(2))
                : 0,
            energy: row ? Number(Number(row.energy_kwh || 0).toFixed(3)) : 0,
        };
    });
}

export function hasRobotConsumptionData(data) {
    return (data || []).some(
        item =>
            Number(item && item.water) > 0 || Number(item && item.energy) > 0
    );
}
